/**
 * GamificationSection Component - Clean Design
 * Streak, badges, and progress display - matches Admin Panel style
 */
import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { toast } from 'sonner';
import { Flame, Award, Share2, Trophy } from 'lucide-react';

const GamificationSection = ({ gamification, isDemo = false }) => {
  const [showBadgesDialog, setShowBadgesDialog] = useState(false);

  const shareBadge = async (badge, platform) => {
    if (isDemo) {
      toast.info('Register to earn and share real badges!');
      return;
    }
    const text = `I earned the "${badge.name}" badge at Exam Trainer! ${badge.icon}\n\n${badge.description}\n\nPrepare for your certification too:`;
    const url = window.location.origin;
    
    const shareUrls = {
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&summary=${encodeURIComponent(text)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}&quote=${encodeURIComponent(text)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(text + ' ' + url)}`
    };

    if (platform === 'copy') {
      try {
        await navigator.clipboard.writeText(text + ' ' + url);
        toast.success('Link copied!');
      } catch (err) {
        toast.error('Copy failed');
      }
    } else {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  if (!gamification) return null;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      {/* Streak Card - Clean Design */}
      <Card className={`border-2 border-orange-200 dark:border-orange-800 bg-orange-50/50 dark:bg-orange-950/20 ${isDemo ? 'relative overflow-hidden' : ''}`}>
        {isDemo && (
          <div className="absolute top-2 right-2 z-10">
            <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300">
              Sample
            </Badge>
          </div>
        )}
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center">
              <Flame className="w-6 h-6 text-orange-500" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-slate-500 dark:text-slate-400">Current Streak</p>
              <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                {gamification.streak?.current || 0}
              </p>
              <p className="text-xs text-slate-400">days in a row</p>
            </div>
          </div>
          {gamification.streak?.longest > 0 && (
            <p className="mt-3 pt-3 border-t border-orange-200 dark:border-orange-800 text-xs text-slate-500">
              Record: {gamification.streak.longest} days
            </p>
          )}
        </CardContent>
      </Card>

      {/* Badges Card - Clean Design */}
      <Dialog open={showBadgesDialog} onOpenChange={setShowBadgesDialog}>
        <DialogTrigger asChild>
          <Card className={`border-2 border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-950/20 cursor-pointer hover:shadow-md transition-all ${isDemo ? 'relative overflow-hidden' : ''}`}>
            {isDemo && (
              <div className="absolute top-2 right-2 z-10">
                <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300">
                  Sample
                </Badge>
              </div>
            )}
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <Award className="w-6 h-6 text-purple-500" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-slate-500 dark:text-slate-400">Badges</p>
                  <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {gamification.badges?.length || 0}
                  </p>
                  <p className="text-xs text-slate-400">earned</p>
                </div>
              </div>
              {gamification.badges?.length > 0 && (
                <div className="flex gap-1 mt-3 pt-3 border-t border-purple-200 dark:border-purple-800">
                  {gamification.badges.slice(0, 5).map((badge, i) => (
                    <span key={i} className="text-xl" title={badge.name}>
                      {badge.icon}
                    </span>
                  ))}
                  {gamification.badges.length > 5 && (
                    <span className="text-slate-400 text-sm">+{gamification.badges.length - 5}</span>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </DialogTrigger>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Award className="w-5 h-5 text-purple-500" />
              My Badges ({gamification.badges?.length || 0})
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {gamification.badges?.length > 0 ? (
              gamification.badges.map((badge, i) => (
                <div key={i} className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-xl">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{badge.icon}</span>
                    <div>
                      <p className="font-semibold">{badge.name}</p>
                      <p className="text-xs text-slate-500">{badge.description}</p>
                    </div>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={() => shareBadge(badge, 'twitter')}
                      title="Share on Twitter"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={() => shareBadge(badge, 'linkedin')}
                      title="Share on LinkedIn"
                    >
                      <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-8 w-8"
                      onClick={() => shareBadge(badge, 'copy')}
                      title="Copy link"
                    >
                      <Share2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-center text-slate-500 py-8">
                No badges earned yet. Start training to earn your first badge!
              </p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Next Badge Progress - Clean Design */}
      <Card className={`border-2 border-emerald-200 dark:border-emerald-800 bg-emerald-50/50 dark:bg-emerald-950/20 ${isDemo ? 'relative overflow-hidden' : ''}`}>
        {isDemo && (
          <div className="absolute top-2 right-2 z-10">
            <Badge variant="outline" className="text-xs bg-amber-50 text-amber-700 border-amber-300">
              Sample
            </Badge>
          </div>
        )}
        <CardContent className="p-4">
          <div className="flex items-center gap-4 mb-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
              {gamification.nextBadges?.[0] ? (
                <span className="text-2xl">{gamification.nextBadges[0].icon}</span>
              ) : (
                <Trophy className="w-6 h-6 text-emerald-500" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-xs text-slate-500 dark:text-slate-400">Next Badge</p>
              {gamification.nextBadges?.[0] ? (
                <p className="font-semibold text-emerald-700 dark:text-emerald-300">
                  {gamification.nextBadges[0].name}
                </p>
              ) : (
                <p className="font-semibold text-emerald-600">All achieved!</p>
              )}
            </div>
          </div>
          {gamification.nextBadges?.[0] && (
            <div>
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>{gamification.nextBadges[0].current}/{gamification.nextBadges[0].target}</span>
                <span>{gamification.nextBadges[0].percent}%</span>
              </div>
              <Progress value={gamification.nextBadges[0].percent} className="h-2" />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default GamificationSection;
